import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ClientApiError } from "../auth/api";
import { Badge, Button, EmptyState, ErrorState, Input, Skeleton } from "../components/ui";
import { getPublicDog, listPublicDogs, type DogDetail, type DogSummary } from "../dogs/api";
import { DogStatusBadge } from "../dogs/DogStatus";
import { TaxonomyAutocomplete } from "../taxonomy/TaxonomyAutocomplete";
import type { PublicTaxonomyItem } from "../taxonomy/api";

const dayFormatter = new Intl.DateTimeFormat("vi-VN", {
  dateStyle: "long",
  timeZone: "Asia/Ho_Chi_Minh",
});

function displayDate(value: string | null): string {
  if (!value) return "Chưa cập nhật";
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? value : dayFormatter.format(date);
}

function sexLabel(value: DogSummary["sex"]): string {
  return value === "male" ? "Đực" : value === "female" ? "Cái" : "Chưa rõ";
}

export function PublicDogDirectoryPage() {
  const [query, setQuery] = useState("");
  const [breed, setBreed] = useState<PublicTaxonomyItem | null>(null);
  const [items, setItems] = useState<DogSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setLoading(true);
      listPublicDogs({ q: query, breedId: breed?.id, cursor, limit: 20 })
        .then((page) => {
          setItems(page.items);
          setNextCursor(page.nextCursor);
          setError(null);
        })
        .catch((caught) => {
          if (caught instanceof ClientApiError) {
            setError({ message: caught.problem.message, requestId: caught.problem.requestId });
          } else {
            setError({ message: "Không thể tải danh sách hồ sơ chó." });
          }
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [breed?.id, cursor, query]);

  return (
    <section className="public-dog-directory page-container">
      <header className="public-directory-heading">
        <p className="eyebrow">WorkingDog VN database</p>
        <h1>Hồ sơ chó nghiệp vụ</h1>
        <p>Chỉ hiển thị hồ sơ đã được kiểm duyệt và publish.</p>
      </header>
      <div className="public-dog-search">
        <Input
          label="Tìm theo tên"
          type="search"
          value={query}
          placeholder="Tên đăng ký hoặc tên gọi"
          onChange={(event) => {
            setQuery(event.target.value);
            setCursor(null);
          }}
        />
        <TaxonomyAutocomplete
          namespace="breed"
          label="Lọc theo giống"
          value={breed}
          onSelect={(item) => {
            setBreed(item);
            setCursor(null);
          }}
        />
      </div>
      {loading ? <Skeleton lines={6} /> : null}
      {!loading && error ? (
        <ErrorState title="Không tải được hồ sơ" description={error.message} requestId={error.requestId} />
      ) : null}
      {!loading && !error && items.length === 0 ? (
        <EmptyState title="Không có kết quả" description="Thử tên hoặc giống khác." />
      ) : null}
      {!loading && !error ? (
        <div className="public-dog-grid">
          {items.map((dog) => (
            <Link key={dog.id} to={`/dogs/${dog.slug}`} className="public-dog-card">
              <div className="dog-avatar-placeholder" aria-hidden="true">
                {dog.registeredName.slice(0, 1).toUpperCase()}
              </div>
              <div>
                <h2>{dog.registeredName}</h2>
                <p>{dog.callName ? `Tên gọi: ${dog.callName}` : dog.breed?.nameVi ?? "Chưa rõ giống"}</p>
                <dl>
                  <div><dt>Giống</dt><dd>{dog.breed?.nameVi ?? "Chưa rõ"}</dd></div>
                  <div><dt>Giới tính</dt><dd>{sexLabel(dog.sex)}</dd></div>
                  <div><dt>Ngày sinh</dt><dd>{displayDate(dog.dateOfBirth)}</dd></div>
                </dl>
              </div>
            </Link>
          ))}
        </div>
      ) : null}
      <div className="dog-cursor-pagination">
        <Button variant="secondary" disabled={!cursor} onClick={() => setCursor(null)}>Trang đầu</Button>
        <Button variant="secondary" disabled={!nextCursor} onClick={() => setCursor(nextCursor)}>Trang sau</Button>
      </div>
    </section>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><dt>{label}</dt><dd>{children || "Chưa cập nhật"}</dd></div>;
}

export function PublicDogProfilePage() {
  const { slug } = useParams();
  const [dog, setDog] = useState<DogDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);

  useEffect(() => {
    if (!slug) return;
    let active = true;
    setLoading(true);
    getPublicDog(slug)
      .then(({ dog: value }) => {
        if (!active) return;
        setDog(value);
        setError(null);
        document.title = `${value.registeredName} — WorkingDog VN`;
      })
      .catch((caught) => {
        if (!active) return;
        if (caught instanceof ClientApiError) {
          setError({ message: caught.problem.message, requestId: caught.problem.requestId });
        } else {
          setError({ message: "Không thể tải hồ sơ chó." });
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (loading) return <div className="page-container"><Skeleton lines={10} /></div>;
  if (error || !dog) {
    return (
      <div className="page-container">
        <ErrorState
          title="Không tìm thấy hồ sơ công khai"
          description={error?.message ?? "Hồ sơ chưa được publish hoặc đã lưu trữ."}
          requestId={error?.requestId}
        />
      </div>
    );
  }

  return (
    <article className="dog-public-profile page-container">
      <header className="dog-profile-header">
        <div className="dog-profile-avatar" aria-label="Ảnh đại diện chưa được cập nhật">
          {dog.registeredName.slice(0, 1).toUpperCase()}
        </div>
        <div className="dog-profile-title">
          <div className="dog-profile-badges">
            <DogStatusBadge status={dog.workflowStatus} />
            {dog.verified ? <Badge tone="info">Đã xác minh</Badge> : <Badge tone="neutral">Chưa xác minh</Badge>}
          </div>
          <p className="eyebrow">{dog.breed?.nameVi ?? "Working dog"}</p>
          <h1>{dog.registeredName}</h1>
          <p className="dog-call-name">{dog.callName ? `Tên gọi: ${dog.callName}` : "Chưa có tên gọi"}</p>
          <div className="dog-profile-primary-line">
            <span>{sexLabel(dog.sex)}</span>
            <span>{displayDate(dog.dateOfBirth)}</span>
            <span>{dog.locationProvince?.nameVi ?? dog.locationCountry?.nameVi ?? "Chưa rõ địa điểm"}</span>
          </div>
        </div>
      </header>

      <section className="dog-profile-priority-grid">
        <div className="dog-profile-card">
          <h2>Phả hệ gần</h2>
          <dl className="dog-quick-grid">
            <Fact label="Chó cha">
              {dog.sire ? <Link to={`/dogs/${dog.sire.slug}`}>{dog.sire.registeredName}</Link> : "Chưa cập nhật"}
            </Fact>
            <Fact label="Chó mẹ">
              {dog.dam ? <Link to={`/dogs/${dog.dam.slug}`}>{dog.dam.registeredName}</Link> : "Chưa cập nhật"}
            </Fact>
            <Fact label="Người gây giống">{dog.breederName}</Fact>
            <Fact label="Kennel">{dog.kennel?.name}</Fact>
          </dl>
        </div>
        <div className="dog-profile-card">
          <h2>Chủ sở hữu và đăng ký</h2>
          <dl className="dog-quick-grid">
            <Fact label="Chủ sở hữu">
              {dog.owner?.publicSlug
                ? <Link to={`/u/${dog.owner.publicSlug}`}>{dog.owner.displayName}</Link>
                : dog.owner?.displayName}
            </Fact>
            <Fact label="Tổ chức">{dog.registrationOrganization?.nameVi}</Fact>
            <Fact label="Số đăng ký">{dog.registrationNumber}</Fact>
            <Fact label="Số phả hệ">{dog.pedigreeNumber}</Fact>
          </dl>
        </div>
      </section>

      <section className="dog-profile-card">
        <h2>Quick facts</h2>
        <dl className="dog-quick-grid dog-quick-grid--wide">
          <Fact label="Giống">{dog.breed?.nameVi}</Fact>
          <Fact label="Màu lông">{dog.color?.nameVi}</Fact>
          <Fact label="Giới tính">{sexLabel(dog.sex)}</Fact>
          <Fact label="Ngày sinh">{displayDate(dog.dateOfBirth)}</Fact>
          <Fact label="Quốc gia">{dog.locationCountry?.nameVi}</Fact>
          <Fact label="Tỉnh/thành">{dog.locationProvince?.nameVi}</Fact>
          <Fact label="Nguồn dữ liệu">{dog.sourceType}</Fact>
          <Fact label="Xác minh">{dog.verifiedAt ? displayDate(dog.verifiedAt) : "Chưa xác minh"}</Fact>
        </dl>
      </section>

      <section className="dog-profile-card dog-profile-biography">
        <h2>Giới thiệu</h2>
        <p>{dog.biography || "Hồ sơ chưa có phần giới thiệu."}</p>
      </section>
    </article>
  );
}
